import { ENDPOINTS } from '@/constants/api'
import { QUERY_KEYS } from '@/constants/query-keys'
import type { QueryOf, RequestBodyOf, ResponseData } from '@/types/api'
import { apiSlice } from './base'

export type Gateway = ResponseData<'/api/v1/gateways/{id}', 'get'>
type GatewaysListData = ResponseData<'/api/v1/gateways/', 'get'>
type GatewaysQuery = QueryOf<'/api/v1/gateways/', 'get'>
type CreateGatewayBody = RequestBodyOf<'/api/v1/gateways/', 'post'>
type UpdateGatewayBody = RequestBodyOf<'/api/v1/gateways/{id}', 'patch'>
type UpdateStatusBody = RequestBodyOf<'/api/v1/gateways/{id}/status', 'patch'>

export const gatewaysApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getGateways: builder.query<GatewaysListData, GatewaysQuery | void>({
      query: (params) => ({ url: ENDPOINTS.GATEWAYS.ROOT, ...(params ? { params } : {}) }),
      transformResponse: (response: { data: GatewaysListData }) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.items.map((g) => ({ type: QUERY_KEYS.GATEWAY, id: g.id }) as const),
              { type: QUERY_KEYS.GATEWAY, id: 'LIST' } as const,
            ]
          : [{ type: QUERY_KEYS.GATEWAY, id: 'LIST' } as const],
    }),
    getGatewayById: builder.query<Gateway, string>({
      query: (id) => ENDPOINTS.GATEWAYS.BY_ID(id),
      transformResponse: (response: { data: Gateway }) => response.data,
      providesTags: (_r, _e, id) => [{ type: QUERY_KEYS.GATEWAY, id }],
    }),
    createGateway: builder.mutation<Gateway, CreateGatewayBody>({
      query: (body) => ({ url: ENDPOINTS.GATEWAYS.ROOT, method: 'POST', body }),
      transformResponse: (response: { data: Gateway }) => response.data,
      invalidatesTags: [{ type: QUERY_KEYS.GATEWAY, id: 'LIST' }],
    }),
    updateGateway: builder.mutation<Gateway, { id: string; body: UpdateGatewayBody }>({
      query: ({ id, body }) => ({ url: ENDPOINTS.GATEWAYS.BY_ID(id), method: 'PATCH', body }),
      transformResponse: (response: { data: Gateway }) => response.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: QUERY_KEYS.GATEWAY, id },
        { type: QUERY_KEYS.GATEWAY, id: 'LIST' },
      ],
    }),
    updateGatewayStatus: builder.mutation<Gateway, { id: string; body: UpdateStatusBody }>({
      query: ({ id, body }) => ({ url: ENDPOINTS.GATEWAYS.STATUS(id), method: 'PATCH', body }),
      transformResponse: (response: { data: Gateway }) => response.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: QUERY_KEYS.GATEWAY, id },
        { type: QUERY_KEYS.GATEWAY, id: 'LIST' },
      ],
    }),
    deleteGateway: builder.mutation<void, string>({
      query: (id) => ({ url: ENDPOINTS.GATEWAYS.BY_ID(id), method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: QUERY_KEYS.GATEWAY, id },
        { type: QUERY_KEYS.GATEWAY, id: 'LIST' },
      ],
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetGatewaysQuery,
  useGetGatewayByIdQuery,
  useCreateGatewayMutation,
  useUpdateGatewayMutation,
  useUpdateGatewayStatusMutation,
  useDeleteGatewayMutation,
} = gatewaysApi
